import { useEffect, useState } from 'react'

/**
 * `sessionStorage`, not `localStorage`, because the bypass is meant to last exactly
 * one session: it dies with the tab, so a support engineer cannot leave a machine
 * permanently exempt from the maintenance page by forgetting about it.
 */
const BYPASS_STORAGE_KEY = 'maintenance-bypass'

/**
 * Storage access throws rather than returning null when it is unavailable -- Safari
 * in private browsing, and any browser with site data blocked -- so every access is
 * guarded. Failing to read the bypass has to leave the maintenance page up.
 */
const readBypass = (): boolean => {
  try {
    return sessionStorage.getItem(BYPASS_STORAGE_KEY) === 'true'
  } catch (error) {
    return false
  }
}

const writeBypass = (): void => {
  try {
    sessionStorage.setItem(BYPASS_STORAGE_KEY, 'true')
  } catch (error) {
    // The bypass still applies to this render; it just will not survive a reload.
  }
}

/**
 * `code` identifies the physical key, so the shortcut works on layouts where `m`
 * sits elsewhere. `key` is the fallback because synthetic events -- Cypress's
 * `trigger`, and anything dispatched by hand -- often carry only `key`.
 */
const isBypassShortcut = (event: KeyboardEvent): boolean =>
  event.ctrlKey &&
  event.shiftKey &&
  (event.code === 'KeyM' || (event.key || '').toLowerCase() === 'm')

/**
 * Ctrl+Shift+M dismisses the maintenance page for the rest of the browser session.
 *
 * This exists so the team can reach the application while maintenance is on for
 * everyone else, without having to flip the flag and expose the app to all users.
 *
 * Must be called below the `isServer()` guard in `MaintenanceGate`: it reads
 * `sessionStorage`, which does not exist during `renderToString`.
 */
export const useMaintenanceBypass = (): boolean => {
  const [bypassed, setBypassed] = useState<boolean>(readBypass)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!isBypassShortcut(event)) return

      // The browser's own Ctrl+Shift+M bindings are not ours to cancel, but any
      // page-level handler competing for the chord is.
      event.preventDefault()
      writeBypass()
      setBypassed(true)
    }

    window.addEventListener('keydown', onKeyDown)

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return bypassed
}
